import * as React from 'react';
import { MoreVertical, Shield, Key, MapPin } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/Popover';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { OrgUnitPicker } from '../../org/components/OrgUnitPicker';
import { useToast } from '../../../components/ui/Toast';
import {
  type StaffMember,
  useRoles,
  useUpdateStaffRole,
  useUpdateStaffBranch,
  useResetStaffPassword,
} from '../network/staff.api';
import { useOrgUnits } from '../../org/network/org.api';

interface StaffActionsProps {
  member: StaffMember;
}

export function StaffActions({ member }: StaffActionsProps) {
  const [menuOpen, setPopoverOpen] = React.useState(false);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = React.useState(false);

  const [selectedRoleId, setSelectedRoleId] = React.useState(member.roleId ?? '');
  const [selectedCompanyWide, setSelectedCompanyWide] = React.useState(member.companyWide ?? false);
  const [selectedOrgUnitId, setSelectedOrgUnitId] = React.useState(member.orgUnitId ?? '');

  const { data: roles } = useRoles();
  const { data: orgUnits } = useOrgUnits();
  const { toast } = useToast();

  const roleMutation = useUpdateStaffRole();
  const branchMutation = useUpdateStaffBranch();
  const resetMutation = useResetStaffPassword();

  const handleResetPassword = async () => {
    try {
      await resetMutation.mutateAsync(member.id);
      toast({
        title: 'Success',
        description: `Password reset initiated for ${member.name}.`,
        type: 'success',
      });
      setResetDialogOpen(false);
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to initiate password reset',
        type: 'error',
      });
      console.error(err);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRoleId) return;
    try {
      await roleMutation.mutateAsync({ userId: member.id, roleId: selectedRoleId, companyWide: selectedCompanyWide });
      toast({
        title: 'Role Updated',
        description: `${member.name}'s role has been updated.`,
        type: 'success',
      });
      setRoleDialogOpen(false);
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update role',
        type: 'error',
      });
      console.error(err);
    }
  };

  const handleUpdateBranch = async () => {
    if (!selectedOrgUnitId) return;
    try {
      await branchMutation.mutateAsync({ userId: member.id, orgUnitId: selectedOrgUnitId });
      toast({
        title: 'Branch Updated',
        description: `${member.name}'s branch has been updated.`,
        type: 'success',
      });
      setBranchDialogOpen(false);
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update branch',
        type: 'error',
      });
      console.error(err);
    }
  };

  const roleOptions = React.useMemo(() => {
    if (!roles) return [];
    return roles.map((r) => ({ value: r.id, label: r.name }));
  }, [roles]);

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0" aria-label="Staff actions">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end">
          <div className="flex flex-col gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPopoverOpen(false);
                setRoleDialogOpen(true);
              }}
              className="flex items-center gap-2 w-full text-left"
            >
              <Shield className="h-4 w-4 text-text-muted" />
              <span>Change Role</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPopoverOpen(false);
                setBranchDialogOpen(true);
              }}
              className="flex items-center gap-2 w-full text-left"
            >
              <MapPin className="h-4 w-4 text-text-muted" />
              <span>Change Branch</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPopoverOpen(false);
                setResetDialogOpen(true);
              }}
              className="flex items-center gap-2 w-full text-left"
            >
              <Key className="h-4 w-4 text-text-muted" />
              <span>Reset Password</span>
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Reset Password Confirmation */}
      <ConfirmDialog
        open={resetDialogOpen}
        title="Reset Password?"
        description={`Are you sure you want to reset the password for ${member.name}? This will send a password reset link to ${member.email}.`}
        onConfirm={handleResetPassword}
        onCancel={() => setResetDialogOpen(false)}
        loading={resetMutation.isPending}
        confirmLabel="Reset"
      />

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormSelect
              label="Select New Role"
              required
              options={[{ value: '', label: 'Select role...' }, ...roleOptions]}
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            />
            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <Input
                type="checkbox"
                checked={selectedCompanyWide}
                onChange={(e) => setSelectedCompanyWide(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-accent-500 focus:ring-accent-500 cursor-pointer"
                disabled={roleMutation.isPending}
              />
              <span className="text-xs">
                <span className="font-semibold text-text-primary block">Company-wide access</span>
                <span className="text-text-secondary">
                  Lifts branch scoping so this role applies across all branches.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => setRoleDialogOpen(false)}
                disabled={roleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateRole}
                disabled={!selectedRoleId}
                loading={roleMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Branch Dialog */}
      <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormField label="Select New Branch" required>
              <OrgUnitPicker
                nodes={orgUnits || []}
                value={selectedOrgUnitId}
                onChange={setSelectedOrgUnitId}
                unitType="BRANCH"
                placeholder="Select branch..."
                disabled={branchMutation.isPending}
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={() => setBranchDialogOpen(false)}
                disabled={branchMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateBranch}
                disabled={!selectedOrgUnitId}
                loading={branchMutation.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
